import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { StoryElement } from "@quintype/components";

import TextElement from "../Story-Elements/TextElement";
import ImageElement from "../Story-Elements/ImageElement";
import TitleElement from "../Story-Elements/TitleElement";
import EmbedElement from "../Story-Elements/EmbedElement";
import ImageCard from "../Story-Elements/ImageCard";
import PartnershipSection from "../Story-Elements/PartnershipSection";

import { getCardType, usesBrandColour, isPartnershipCard, getCardAttribute } from "./card-utils";
import { useSelector, useDispatch } from "react-redux";
import { navigateTo } from "../../../arrow/utils/utils";
import CloseIcon from "../../atoms/CloseIcon";
import { SignInModal } from "../../layouts/header/sign-in-modal";

import "./disclaimer-modal.m.css";
import "../text-story/text-story.m.css";

/**
 * Renders a single story element based on its type
 * @param {Object} options - Render options
 * @param {Object} options.element - The story element to render
 * @param {Object} options.story - The full story object
 * @param {number} options.cardIndex - Index of the parent card
 * @param {number} options.elementIndex - Index of this element within the card
 * @param {boolean} options.useBrandColour - Whether to apply brand colour styling
 * @param {boolean} options.usePartnershipStyle - Whether to apply partnership styling
 * @returns {React.Element|null}
 */
const renderStoryElement = ({
  element,
  story,
  cardIndex,
  elementIndex,
  useBrandColour,
  usePartnershipStyle = false,
  isLeadershipFocus,
  onFileClick,
  card,
}) => {
  const key = `${card.id || cardIndex}-${elementIndex}`;
  const { type } = element;

  switch (type) {
    case "text":
      return (
        <TextElement
          key={key}
          element={element}
          useBrandColour={useBrandColour}
          usePartnershipStyle={usePartnershipStyle}
          isLeadershipFocus={isLeadershipFocus}
        />
      );

    case "image":
      return (
        <ImageElement
          key={key}
          element={element}
          hasLeftImage={false}
          hasRightImage={false}
          isLeadership={isLeadershipFocus}
        />
      );

    case "title":
      return (
        <TitleElement
          key={key}
          element={element}
          usePartnershipStyle={usePartnershipStyle}
        />
      );

    case "jsembed":
    case "youtube-video":
      return <EmbedElement key={key} element={element} story={story} />;

    case "also-read":
      return null;

    case "file":
      const fileName = get(element, ["metadata", "file-name"], "Download PDF");
      const fileUrl = get(element, ["url"], "#");
      const isDisclaimerRequired = getCardAttribute(card, "disclaimerdisplay");

      const handleDownloadClick = (e) => {
        if (isDisclaimerRequired) {
          e.preventDefault();
          onFileClick({ title: fileName, url: fileUrl });
        }
      };

      return (
        <div key={key} id="download-report-container">
          <a
            href={fileUrl}
            id="download-report-btn"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDownloadClick}
          >
            {fileName}
          </a>
        </div>
      );

    default:
      return (
        <div key={key} styleName="story-element">
          <StoryElement element={element} story={story} />
        </div>
      );
  }
};

/**
 * StoryCard component - renders a single card from a story
 * @param {Object} props
 * @param {Object} props.card - The card object to render
 * @param {number} props.cardIndex - Index of this card
 * @param {Object} props.story - The full story object
 * @param {boolean} props.applyPartnershipStyleToFirstTitle - Whether to apply partnership styling to first title/text element
 */
const StoryCard = ({
  card,
  cardIndex,
  story,
  applyPartnershipStyleToFirstTitle = false,
  isLeadershipFocus,
}) => {
  const [activeFile, setActiveFile] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = React.useState(false);

  const storyElements = get(card, ["story-elements"], []);
  const useBrandColour = usesBrandColour(card);
  const cardType = getCardType(card);

  const handleFileClick = (file) => {
    setActiveFile(file);
    setShowModal(true);
  };

  const handleDownloadSuccess = () => {
    if (activeFile && activeFile.url) {
      window.open(activeFile.url, "_blank");
    }
  };

  // Render as ImageCard if it has image layout attributes
  if (cardType) {
    return (
      <ImageCard
        card={card}
        cardIndex={cardIndex}
        cardType={cardType}
      />
    );
  }

  // Render as PartnershipSection if it matches the pattern
  if (isPartnershipCard(card)) {
    const [titleElement, galleryElement] = storyElements;
    return (
      <PartnershipSection
        titleElement={titleElement}
        galleryElement={galleryElement}
        useBrandColour={useBrandColour}
      />
    );
  }

  // Render as regular card with story elements
  return (
    <div styleName="card">
      {storyElements.map((element, elementIndex) => {
        // Apply partnership style to the first element (title or text) if requested
        const usePartnershipStyle =
          applyPartnershipStyleToFirstTitle &&
          elementIndex === 0 &&
          (element.type === "title" || element.type === "text");

        return renderStoryElement({
          element,
          story,
          cardIndex,
          elementIndex,
          useBrandColour,
          usePartnershipStyle,
          isLeadershipFocus,
          onFileClick: handleFileClick,
          card,
        });
      })}

      <DisclaimerModal
        isVisible={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleDownloadSuccess}
        pdfTitle={activeFile?.title}
        pdfUrl={activeFile?.url}
        card={card}
        onSignInClick={() => {
          setShowModal(false);
          setIsSignInModalOpen(true);
        }}
      />

      <SignInModal 
        isOpen={isSignInModalOpen} 
        onClose={() => setIsSignInModalOpen(false)} 
      />
    </div>
  );
};

// Internal DisclaimerModal component to avoid dependency issues
const DisclaimerModal = ({ isVisible, onClose, onSuccess, pdfTitle, pdfUrl, card, onSignInClick }) => {
  const dispatch = useDispatch();
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const [loading, setLoading] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    agreed: false,
  });

  // Extract partner name if available
  const partnerName = get(card, ["metadata", "attributes", "partner-name", 0]) || 
                      get(card, ["metadata", "attributes", "partnername", 0]) || 
                      "its relevant partner";

  React.useEffect(() => {
    if (member || subscriberMetadata) {
      const firstName = get(subscriberMetadata, ["first-name"], "");
      const lastName = get(subscriberMetadata, ["last-name"], "");
      const subscriberName = get(subscriberMetadata, ["name"], "");
      const subscriberEmail = get(subscriberMetadata, ["email"], "");
      const subscriberPhone = get(subscriberMetadata, ["phone"], "");

      const combinedName = `${firstName} ${lastName}`.trim();

      setFormData((prev) => ({
        ...prev,
        name: combinedName || subscriberName || get(member, ["name"]) || get(member, ["display-name"]) || "",
        email: subscriberEmail || get(member, ["email"]) || "",
        phone: subscriberPhone || get(member, ["metadata", "phone-number"]) || get(member, ["login-phone-number"]) || get(member, ["phone-number"]) || "",
      }));
    }
  }, [member, subscriberMetadata]);

  React.useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      setIsSuccess(false); // Reset success state when modal opens
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const validate = () => {
    const newErrors = {};
    if (!String(formData.name || "").trim()) newErrors.name = "Name is required";
    if (!String(formData.email || "").trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!String(formData.phone || "").trim()) newErrors.phone = "Phone is required";
    if (!String(formData.company || "").trim()) newErrors.company = "Company is required";
    if (!String(formData.designation || "").trim()) newErrors.designation = "Designation is required";
    if (!formData.agreed) newErrors.agreed = "You must agree";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await fetch("/download-pdf-smtp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          pdfTitle: pdfTitle || "PDF Document",
          pdfUrl,
        }),
      });

      setIsSuccess(true);
      
      // Wait for animation to play before closing and opening PDF
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 3000);

    } catch (error) {
      console.error("Submission error:", error);
      onClose();
    } finally {
      setLoading(false);
    }
  };
  
  const handleSignInOpen = () => {
    onSignInClick();
  };

  const inputClass = (name) => {
    return errors[name] ? "input inputError" : "input";
  };

  const isFormFilled = 
    String(formData.name || "").trim() !== "" &&
    String(formData.email || "").trim() !== "" &&
    String(formData.phone || "").trim() !== "" &&
    String(formData.company || "").trim() !== "" &&
    String(formData.designation || "").trim() !== "";

  return (
    <div styleName="modalOverlay" onClick={onClose}>
      <div styleName="modalContent" onClick={(e) => e.stopPropagation()}>
        <div styleName="closeBtn" onClick={onClose}>
          <CloseIcon size={12} strokeWidth={1.25} color="var(--Icon-icon-primary, #242424)" />
        </div>
        
        {isSuccess ? (
          <div styleName="successContainer">
            <div styleName="checkmarkWrapper">
              <svg styleName="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle styleName="checkmarkCircle" cx="26" cy="26" r="25" fill="none" />
                <path styleName="checkmarkCheck" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
              </svg>
            </div>
            <h2 styleName="successText">Success!</h2>
            <p styleName="successSubtext">Your details have been submitted. Opening PDF...</p>
          </div>
        ) : (
          <>
            <h2 styleName="title">Enter your details</h2>

            <form onSubmit={handleSubmit} styleName="form">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              name="name"
              placeholder="Name*"
              value={formData.name}
              onChange={handleChange}
              styleName={inputClass("name")}
            />
          </div>

          <div styleName="inputGroup">
            <div styleName="field fieldLong">
              <input
                type="email"
                name="email"
                placeholder="Email*"
                value={formData.email}
                onChange={handleChange}
                styleName={inputClass("email")}
              />
            </div>
            <div styleName="field fieldShort">
              <input
                type="text"
                name="phone"
                placeholder="Mobile*"
                value={formData.phone}
                onChange={handleChange}
                styleName={inputClass("phone")}
              />
            </div>
          </div>

          <div styleName="inputGroup">
            <div styleName="field fieldLong">
              <input
                type="text"
                name="company"
                placeholder="Company*"
                value={formData.company}
                onChange={handleChange}
                styleName={inputClass("company")}
              />
            </div>
            <div styleName="field fieldShort">
              <input
                type="text"
                name="designation"
                placeholder="Designation*"
                value={formData.designation}
                onChange={handleChange}
                styleName={inputClass("designation")}
              />
            </div>
          </div>
          
          <div styleName="checkboxContainer">
            <input
              type="checkbox"
              id="agreed"
              name="agreed"
              checked={formData.agreed}
              onChange={handleChange}
              styleName="checkbox"
              disabled={!isFormFilled}
            />
            <label htmlFor="agreed" styleName={isFormFilled ? "label" : "label labelDisabled"}>
              I agree that IMA India may share my contact information with {partnerName}
            </label>
          </div>

          <button 
            type="submit" 
            disabled={loading || !isFormFilled || !formData.agreed}
            styleName="submitBtn"
          >
            {loading ? "Processing..." : "Submit"}
          </button>

            <p styleName="footer">
              <span styleName="signIn" onClick={handleSignInOpen}>Sign in</span> if you are already a member
            </p>
          </form>
        </>
        )}
      </div>
    </div>
  );
};

StoryCard.propTypes = {
  card: PropTypes.object.isRequired,
  cardIndex: PropTypes.number.isRequired,
  story: PropTypes.object.isRequired,
  applyPartnershipStyleToFirstTitle: PropTypes.bool,
};

export default StoryCard;
